import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faTimes } from '@fortawesome/free-solid-svg-icons';

interface NotificationButton {
  text: string;
  onClick: () => void;
  className?: string;
}

interface NotificationProps {
  icon: IconDefinition;
  text: string;
  buttons: NotificationButton[];
  audio?: string;
  onDismiss: () => void;
  index: number;
}

const Notification: React.FC<NotificationProps> = ({ icon, text, buttons, audio, onDismiss, index }) => {
  useEffect(() => {
    if (audio) {
      const audioElement = new Audio(audio);
      audioElement.play();
    }
  }, [audio]);

  const notificationStyle: React.CSSProperties = {
    position: 'fixed',
    top: `calc(10dvh + ${index * (30 + 100)}px)`, // 30px gap between notifications
    left: '50%',
    transform: 'translateX(-50%)',
    width: '97dvw',
    maxWidth: '600px',
    backgroundColor: '#F5F5F5',
    padding: '1rem',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.15)', // much stronger shadow
    borderRadius: '0.5rem',
    direction: 'rtl',
    zIndex: 1000 - index,
    border: '1px solid #e2e8f0'
  };

  const contentStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: 'bold'
  };

  const iconTextStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const buttonContainerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '1rem',
    gap: '0.5rem',
    borderRadius: '8px'
  };

  const buttonStyle: React.CSSProperties = {    
    padding: '0.5rem 1rem',
    cursor: 'pointer'
  };

  return (
    <div style={notificationStyle}>
      <div style={contentStyle}>
        <div style={iconTextStyle}>
          <FontAwesomeIcon icon={icon} style={{ fontSize: '1.5rem' }} />
          <span>{text}</span>
        </div>
        <button 
          data-testid="notifications-close-button"
          onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <FontAwesomeIcon icon={faTimes} style={{ fontSize: '1.25rem'}} />
        </button>
      </div>
      <div style={buttonContainerStyle}>
        {buttons.map((button, index) => (
          <button
            key={index}
            onClick={() => {
              button.onClick();
              onDismiss();
            }}
            className={`notification-button ${button.className || ''}`}
            style={{
              ...buttonStyle
            }}
          >
            {button.text}
          </button>
        ))}
      </div>
    </div>
  );
};

interface NotificationManagerProps {
  children: React.ReactNode;
}

interface NotificationData {
  icon: IconDefinition;
  text: string;
  buttons: NotificationButton[];
  audio?: string;
  onDismiss?: () => void;
}

export const NotificationContext = React.createContext<{
  showNotification: (props: NotificationData) => void;
} | undefined>(undefined);

export const NotificationProvider: React.FC<NotificationManagerProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<(NotificationData & { id: string })[]>([]);
  const idCounterRef = useRef(0);
  const showNotification = (props: NotificationData) => {
    const id = `notification-${Date.now()}-${idCounterRef.current++}`;
    setNotifications(prev => [...prev, { ...props, id }]);
  };

  const dismissNotification = (id: string) => {
    const notification = notifications.find(n => n.id === id);
    if (notification?.onDismiss) {
      notification.onDismiss();
    }
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      {notifications.map((notification, index) => (
        <Notification
          key={notification.id}
          {...notification}
          onDismiss={() => dismissNotification(notification.id)}
          index={index}
        />
      ))}
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const context = React.useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};